import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Skeleton,
  Fade,
  Slide,
  Badge,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  MonetizationOn as RefundIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  GetApp as ExportIcon
} from '@material-ui/icons';
import moment from 'moment';
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)',
  },
  card: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  successButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    },
  },
  tableHead: {
    backgroundColor: '#1e293b',
    color: '#22c55e',
    fontWeight: 'bold',
    borderBottom: '2px solid #2d3748'
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(34, 197, 94, 0.05)'
    }
  },
  avatar: {
    backgroundColor: '#22c55e',
    width: 32,
    height: 32,
    fontSize: '0.8rem'
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#ffffff',
      borderRadius: theme.spacing(2),
      border: '1px solid #2d3748',
      maxWidth: '900px',
      width: '100%'
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#22c55e',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#22c55e',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#22c55e',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#22c55e',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#22c55e',
      },
    },
    '& .MuiSelect-select': {
      color: '#e2e8f0',
    },
    '& .MuiSvgIcon-root': {
      color: '#a0aec0',
    },
  }
}));

export default function RefundHistory() {
  const classes = useStyles();
  const theme = useTheme();
  const [refunds, setRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({
    totalRefundAmount: 0,
    pendingRefunds: 0,
    completedRefunds: 0,
    rejectedRefunds: 0
  });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  
  // Dialog states
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  
  const { isCollapse } = useCollapseDrawer();
  
  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const fetchRefundHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await admin_api.get_refund_history(page + 1, rowsPerPage, statusFilter);
      
      if (response && response.data && response.data.success) {
        const refundsData = response.data.data?.refunds || [];
        const paginationData = response.data.data?.pagination || { currentPage: page + 1, totalPages: 0, totalRecords: 0 };
        const summaryData = response.data.data?.summary || {
          totalRefundAmount: 0,
          pendingRefunds: 0,
          completedRefunds: 0,
          rejectedRefunds: 0
        };
        
        setRefunds(refundsData);
        setTotalCount(paginationData.totalRecords);
        setSummary(summaryData);
      } else {
        console.warn('[RefundHistory] API returned unsuccessful response:', response);
        setError(response?.data?.message || 'Failed to fetch refund history');
        setRefunds([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('[RefundHistory] Error fetching history:', error);
      setError('Failed to fetch refund history. Please try again.');
      setRefunds([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  // Filter refunds based on search and date
  useEffect(() => {
    let filtered = [...refunds];
    
    if (searchQuery) {
      filtered = filtered.filter(refund => 
        refund.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        refund.court_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        refund.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
      
      filtered = filtered.filter(refund => {
        if (!refund.processed_at) return false;
        
        const processedDate = new Date(refund.processed_at);
        const phDate = new Date(processedDate.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
        
        switch (dateRange) {
          case 'today':
            return phDate.getDate() === today.getDate() && 
                   phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return phDate >= weekAgo && phDate <= today;
          case 'month':
            return phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }
    
    setFilteredRefunds(filtered);
  }, [refunds, searchQuery, dateRange]);

  useEffect(() => {
    fetchRefundHistory();
  }, [fetchRefundHistory]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (refund) => {
    setSelectedRefund(refund);
    setOpenDetailsDialog(true);
  };

  const formatDateToPH = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).utcOffset('+08:00').format('MMM D, YYYY h:mm A');
  };

  const convertTo12Hour = (timeString) => {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getCancelTypeDisplay = (cancelType) => {
    const types = {
      emergency: 'Emergency',
      change_plans: 'Change of Plans',
      double_booking: 'Double Booking',
      weather: 'Weather',
      other: 'Other'
    };
    return types[cancelType] || cancelType;
  };

  const getRefundStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      case 'rejected':
        return '#ef4444';
      case 'pending':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getRefundStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon style={{ color: '#22c55e' }} />;
      case 'rejected':
        return <RejectedIcon style={{ color: '#ef4444' }} />;
      case 'pending':
        return <PendingIcon style={{ color: '#f59e0b' }} />;
      default:
        return <PendingIcon style={{ color: '#6b7280' }} />;
    }
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: theme.spacing(4) }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Refund History
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    Track and manage all refund transactions
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', gap: 16 }}>
                  <Button
                    variant="contained"
                    startIcon={<ExportIcon />}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: theme.spacing(3),
                    }}
                  >
                    Export
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRefundHistory}
                    disabled={loading}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: theme.spacing(3),
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: theme.spacing(4) }}>
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <MoneyIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Refunds
                    </Typography>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      ₱{summary.totalRefundAmount.toLocaleString()}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      All approved refunds
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <Badge badgeContent={summary.pendingRefunds} color="error" max={99}>
                      <PendingIcon style={{ fontSize: 40, color: '#f59e0b', marginBottom: 16 }} />
                    </Badge>
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Pending
                    </Typography>
                    <Typography variant="h4" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {summary.pendingRefunds}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Awaiting processing
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <CompletedIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Completed
                    </Typography>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      {summary.completedRefunds}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Successfully processed
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Slide direction="up" in timeout={1600}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <RejectedIcon style={{ fontSize: 40, color: '#ef4444', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Rejected
                    </Typography>
                    <Typography variant="h4" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {summary.rejectedRefunds}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Declined refunds
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper className={classes.paper} style={{ padding: theme.spacing(3), marginBottom: theme.spacing(4) }}>
            <Typography variant="h6" gutterBottom style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: theme.spacing(3) }}>
              Filter Refunds
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search refunds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" className={classes.formControl}>
                  <InputLabel>Refund Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Refund Status"
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" className={classes.formControl}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                  <Typography 
                    variant="body2" 
                    style={{ color: '#a0aec0', alignSelf: 'center' }}
                  >
                    {filteredRefunds.length} refund{filteredRefunds.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Refunds Table */}
          <Slide direction="up" in timeout={1800}>
            <TableContainer component={Paper} className={classes.paper}>
              {loading ? (
                <Box style={{ padding: theme.spacing(4) }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <Skeleton variant="circle" width={40} height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="30%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="40%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} style={{ backgroundColor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : filteredRefunds.length === 0 ? (
                <Box style={{ padding: theme.spacing(6), textAlign: 'center' }}>
                  <AssignmentIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                    No refund history found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 24 }}>
                    No refund records match your current filters.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={fetchRefundHistory}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh List
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHead}>Customer</TableCell>
                      <TableCell className={classes.tableHead}>Booking</TableCell>
                      <TableCell className={classes.tableHead}>Court & Date</TableCell>
                      <TableCell className={classes.tableHead}>Cancellation</TableCell>
                      <TableCell className={classes.tableHead}>Refund Amount</TableCell>
                      <TableCell className={classes.tableHead}>Status</TableCell>
                      <TableCell className={classes.tableHead}>Processed</TableCell>
                      <TableCell className={classes.tableHead} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRefunds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((refund, index) => (
                      <Fade in timeout={300 + index * 100} key={refund.refund_id || index}>
                        <TableRow className={classes.tableRow}>
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <Avatar className={classes.avatar}>
                                {(refund.user_name || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                  {refund.user_name || 'Unknown User'}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {refund.user_email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {refund.booking_id}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              #{refund.refund_id}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                {refund.court_name}
                              </Typography>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <EventIcon style={{ fontSize: 14, color: '#22c55e' }} />
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {moment(refund.reservation_date).format('MMM D, YYYY')}
                                </Typography>
                              </Box>
                              <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                {convertTo12Hour(refund.time_from)} - {convertTo12Hour(refund.time_to)}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {getCancelTypeDisplay(refund.cancel_type)}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {moment(refund.requested_at).fromNow()}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="h6" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                              ₱{parseFloat(refund.refund_amount || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              of ₱{parseFloat(refund.total_amount || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {getRefundStatusIcon(refund.refund_status)}
                              <Chip 
                                label={refund.refund_status?.toUpperCase() || 'PENDING'} 
                                size="small" 
                                style={{
                                  backgroundColor: `rgba(${getRefundStatusColor(refund.refund_status).slice(1, 3)}, ${getRefundStatusColor(refund.refund_status).slice(3, 5)}, ${getRefundStatusColor(refund.refund_status).slice(5, 7)}, 0.1)`,
                                  color: getRefundStatusColor(refund.refund_status),
                                  border: `1px solid ${getRefundStatusColor(refund.refund_status)}`
                                }}
                              />
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              {formatDateToPH(refund.processed_at)}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {refund.processed_by_name || 'System'}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton 
                                style={{ color: '#22c55e' }}
                                onClick={() => handleViewDetails(refund)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {filteredRefunds.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredRefunds.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  style={{
                    color: '#e2e8f0',
                    borderTop: '1px solid #2d3748',
                  }}
                />
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Refund Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        className={classes.dialog}
      >
        {selectedRefund && (
          <>
            <DialogTitle style={{ 
              borderBottom: '1px solid #2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              color: '#22c55e'
            }}>
              <RefundIcon />
              Refund Details
              <Typography variant="body2" style={{ marginLeft: 'auto', color: '#a0aec0' }}>
                Refund ID: #{selectedRefund.refund_id}
              </Typography>
            </DialogTitle>
            
            <DialogContent style={{ padding: theme.spacing(4) }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#22c55e', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Customer & Booking Information
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Customer:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {selectedRefund.user_name}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#a0aec0', display: 'block' }}>
                        {selectedRefund.user_email}
                      </Typography>
                      {selectedRefund.user_phone && (
                        <Typography variant="caption" style={{ color: '#a0aec0' }}>
                          {selectedRefund.user_phone}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Booking ID:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {selectedRefund.booking_id}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Court:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {selectedRefund.court_name}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#a0aec0' }}>
                        {selectedRefund.court_location}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Date & Time:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {moment(selectedRefund.reservation_date).format('MMMM D, YYYY')}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                        {convertTo12Hour(selectedRefund.time_from)} - {convertTo12Hour(selectedRefund.time_to)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#22c55e', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Financial Information
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Original Payment:</Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#06b6d4', marginBottom: 4 }}>
                        ₱{parseFloat(selectedRefund.total_amount || 0).toLocaleString()}
                      </Typography>
                      {selectedRefund.payment_method && (
                        <Typography variant="caption" style={{ color: '#a0aec0' }}>
                          via {selectedRefund.payment_method.toUpperCase()}
                          {selectedRefund.reference_number && ` • Ref: ${selectedRefund.reference_number}`}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Refund Amount:</Typography>
                      <Typography variant="h4" style={{ fontWeight: 'bold', color: '#22c55e', marginBottom: 4 }}>
                        ₱{parseFloat(selectedRefund.refund_amount || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#a0aec0' }}>
                        {selectedRefund.refund_amount && selectedRefund.total_amount 
                          ? `${((parseFloat(selectedRefund.refund_amount) / parseFloat(selectedRefund.total_amount)) * 100).toFixed(1)}% of total amount`
                          : 'Percentage calculation unavailable'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Refund Status:</Typography>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        {getRefundStatusIcon(selectedRefund.refund_status)}
                        <Chip 
                          label={selectedRefund.refund_status?.toUpperCase() || 'PENDING'} 
                          size="medium" 
                          style={{
                            backgroundColor: `rgba(${getRefundStatusColor(selectedRefund.refund_status).slice(1, 3)}, ${getRefundStatusColor(selectedRefund.refund_status).slice(3, 5)}, ${getRefundStatusColor(selectedRefund.refund_status).slice(5, 7)}, 0.1)`,
                            color: getRefundStatusColor(selectedRefund.refund_status),
                            border: `1px solid ${getRefundStatusColor(selectedRefund.refund_status)}`,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3) }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#22c55e', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Cancellation & Processing Details
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Cancellation Reason:</Typography>
                          <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                            {getCancelTypeDisplay(selectedRefund.cancel_type)}
                          </Typography>
                          <Box style={{ 
                            backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                            padding: 12, 
                            borderRadius: 4,
                            border: '1px solid rgba(34, 197, 94, 0.2)',
                            marginTop: 8
                          }}>
                            <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                              {selectedRefund.reason}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {selectedRefund.additional_notes && (
                          <Box style={{ marginBottom: 16 }}>
                            <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Additional Notes:</Typography>
                            <Box style={{ 
                              backgroundColor: 'rgba(6, 182, 212, 0.1)', 
                              padding: 12, 
                              borderRadius: 4,
                              border: '1px solid rgba(6, 182, 212, 0.2)',
                              marginTop: 8
                            }}>
                              <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                                {selectedRefund.additional_notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Processing Timeline:</Typography>
                          <Box style={{ marginTop: 8 }}>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              <strong>Requested:</strong> {formatDateToPH(selectedRefund.requested_at)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#e2e8f0', marginBottom: 4 }}>
                              <strong>Approved:</strong> {formatDateToPH(selectedRefund.processed_at)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                              <strong>Processed by:</strong> {selectedRefund.processed_by_name || 'System'}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {selectedRefund.admin_notes && (
                          <Box>
                            <Typography variant="subtitle2" style={{ color: '#a0aec0' }}>Admin Notes:</Typography>
                            <Box style={{ 
                              backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                              padding: 12, 
                              borderRadius: 4,
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                              marginTop: 8
                            }}>
                              <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                                {selectedRefund.admin_notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748' }}>
              <Button 
                onClick={() => setOpenDetailsDialog(false)}
                style={{ color: '#a0aec0' }}
                startIcon={<CloseIcon />}
              >
                Close
              </Button>
              <Typography variant="body2" style={{ color: '#a0aec0', marginLeft: 'auto' }}>
                Total Refund: ₱{parseFloat(selectedRefund.refund_amount || 0).toLocaleString()}
              </Typography>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}